import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import BkTable from '../../../table/BkTable';
import { JobDocument, JobStatusType } from '../../../../pages/dashboard/util';
import { Column } from 'src/utils/constants/table-constants';
import { Typography } from 'src/components/new/Typography';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { JOB_DOCUMENT_ARCHIVAL_DELAY_HOURS } from 'src/utils/constant';
import { apiInstance } from '../../../../api/apiInstance';
import EditDocumentsButton from './EditDocumentsButton';
import EditableDocumentList from './EditableDocumentList';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';

// A pending document that hasn't been uploaded yet
export interface PendingDocument {
  tempId: string;
  file: File;
  filename: string;
  description: string;
  refNumber: string;
  documentType: string;
  paperColor: string | null;
}

export type LocalDocumentItem = JobDocument | PendingDocument;

export const isPendingDocument = (doc: LocalDocumentItem): doc is PendingDocument => {
  return 'tempId' in doc;
};

export interface JobDocumentsHandle {
  hasUnsavedChanges: () => boolean;
  hasActiveInput: () => boolean;
  saveChanges: () => Promise<boolean>;
  discardChanges: () => void;
}

interface JobDocumentsProps {
  documents: JobDocument[];
  onDownloadDocument: (filename: string, id: number) => Promise<void>;
  isCosAdmin: boolean;
  jobStatus: JobStatusType;
  jobId: number;
  onDocumentsChange?: () => void;
  /**
   * Reports whether the document section currently has unsaved edits.
   * Consumed by the parent to gate the "Move to In Queue" status transition
   * (admins may only move a job to In Queue once all document changes are saved).
   */
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onInvoiceRecalculated?: (invoice: any) => void;
  onSaveComplete?: () => void;
  currentInvoiceTotal?: number;
}

const columns = [
  { headerName: 'Files', field: 'files' },
  { headerName: 'Description', field: 'description' },
  { headerName: 'ECF Docket Ref. No.', field: 'ref' },
  { headerName: 'Document Type', field: 'documentType' },
  { headerName: 'Paper Color', field: 'paperColor' },
  { headerName: 'Pages', field: 'pages' },
];

const isLinkExpired = (createdAt: string) => {
  return (Date.now() - new Date(createdAt).getTime()) / 1000 / 60 / 60 >= JOB_DOCUMENT_ARCHIVAL_DELAY_HOURS;
};

const JobDocuments = forwardRef<JobDocumentsHandle, JobDocumentsProps>(({ documents, onDownloadDocument, isCosAdmin, jobStatus, jobId, onDocumentsChange, onInvoiceRecalculated, onSaveComplete, currentInvoiceTotal, onUnsavedChangesChange }, ref) => {
  const [data, setData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [localDocuments, setLocalDocuments] = useState<LocalDocumentItem[]>([]);
  const [originalDocumentIds, setOriginalDocumentIds] = useState<number[]>([]);
  const [descriptionChanges, setDescriptionChanges] = useState<Map<number, string>>(new Map());
  const [refNumberChanges, setRefNumberChanges] = useState<Map<number, string>>(new Map());
  const [documentTypeChanges, setDocumentTypeChanges] = useState<Map<number, { type: string; paperColor?: string }>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const prevJobStatusRef = useRef<JobStatusType>(jobStatus);
  const hasSavedRef = useRef(false);
  // Keeps the latest saved documents so re-entering edit mode uses the correct state
  const savedDocumentsRef = useRef<JobDocument[] | null>(null);

  useEffect(() => {
    if (hasSavedRef.current) {
      hasSavedRef.current = false;
      return;
    }
    const transformedData = documents.map((document) => ({
      files: document.storedFile.filename,
      description: document.docketDescription,
      ref: document.docketReferenceNumber,
      documentType: document.type,
      paperColor: document.paperColor,
      pages: document.pageCount,
      id: document.id,
      createdAt: document.createdAt,
    }));
    setData(transformedData);
    // Reset saved ref since we got fresh data from the server
    savedDocumentsRef.current = null;
  }, [documents]);

  const isOnHoldStatus = (status: JobStatusType) => status === 'on_hold' || status === 'on_hold_manual';

  useEffect(() => {
    if (isEditMode && isOnHoldStatus(prevJobStatusRef.current) && !isOnHoldStatus(jobStatus)) {
      setIsEditMode(false);
      setError('Job is no longer On Hold');
    }
    prevJobStatusRef.current = jobStatus;
  }, [jobStatus, isEditMode]);

  const handleEnterEditMode = () => {
    const docs = savedDocumentsRef.current ?? documents;
    setLocalDocuments([...docs]);
    setOriginalDocumentIds(docs.map(d => d.id));
    setDescriptionChanges(new Map());
    setRefNumberChanges(new Map());
    setDocumentTypeChanges(new Map());
    setIsEditMode(true);
    setError(null);
  };

  // Deferred upload: just store the File locally
  const handleAddDocument = async (file: File) => {
    // Check for duplicate files, consistent with customer upload flow
    const fileKey = `${file.name}_${file.size}`;
    const existingKeys = localDocuments.map((doc) =>
      isPendingDocument(doc) ? `${doc.filename}_${doc.file.size}` : doc.storedFile.filename,
    );
    const existingFilenames = localDocuments
      .filter((doc) => !isPendingDocument(doc))
      .map((doc) => (doc as JobDocument).storedFile.filename);

    if (existingKeys.includes(fileKey) || existingFilenames.includes(file.name)) {
      setError('Cannot add the same file twice.');
      return;
    }

    const pendingDoc: PendingDocument = {
      tempId: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      filename: file.name,
      description: '',
      refNumber: '',
      documentType: '',
      paperColor: null,
    };
    setLocalDocuments((prev) => [pendingDoc, ...prev]);
  };

  const handleLocalReorder = (reorderedDocuments: LocalDocumentItem[]) => {
    setLocalDocuments(reorderedDocuments);
  };

  const handleDeleteDocument = (id: number | string) => {
    setLocalDocuments((prev) =>
      prev.filter((doc) => {
        if (isPendingDocument(doc)) return doc.tempId !== id;
        return doc.id !== id;
      }),
    );
    // Remove description change if document is deleted
    if (typeof id === 'number') {
      setDescriptionChanges((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setRefNumberChanges((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDescriptionChange = (id: number | string, newDescription: string) => {
    if (typeof id === 'number') {
      // Find original value to check if it actually changed
      const sourceDocuments = savedDocumentsRef.current ?? documents;
      const originalDoc = sourceDocuments.find(d => d.id === id);
      const originalDescription = originalDoc?.docketDescription ?? '';

      setDescriptionChanges((prev) => {
        const next = new Map(prev);
        if (newDescription === originalDescription) {
          next.delete(id);
        } else {
          next.set(id, newDescription);
        }
        return next;
      });
      // Also update the local document so the UI reflects the change
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (!isPendingDocument(doc) && doc.id === id) {
            return { ...doc, docketDescription: newDescription };
          }
          return doc;
        }),
      );
    } else {
      // Pending document — update description in local state
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (isPendingDocument(doc) && doc.tempId === id) {
            return { ...doc, description: newDescription };
          }
          return doc;
        }),
      );
    }
  };

  const handleRefNumberChange = (id: number | string, newRefNumber: string) => {
    if (typeof id === 'number') {
      const sourceDocuments = savedDocumentsRef.current ?? documents;
      const originalDoc = sourceDocuments.find(d => d.id === id);
      const originalRefNumber = originalDoc?.docketReferenceNumber ?? '';

      setRefNumberChanges((prev) => {
        const next = new Map(prev);
        if (newRefNumber === originalRefNumber) {
          next.delete(id);
        } else {
          next.set(id, newRefNumber);
        }
        return next;
      });
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (!isPendingDocument(doc) && doc.id === id) {
            return { ...doc, docketReferenceNumber: newRefNumber };
          }
          return doc;
        }),
      );
    } else {
      // Pending document — update refNumber in local state
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (isPendingDocument(doc) && doc.tempId === id) {
            return { ...doc, refNumber: newRefNumber };
          }
          return doc;
        }),
      );
    }
  };

  const handleDocumentTypeChange = (id: number | string, newType: string, paperColor?: string) => {
    const normalizedPaperColor = newType === 'ballot' ? (paperColor || 'standard') : undefined;

    if (typeof id === 'number') {
      const sourceDocuments = savedDocumentsRef.current ?? documents;
      const originalDoc = sourceDocuments.find(d => d.id === id);
      const originalType = originalDoc?.type ?? '';
      const originalPaperColor = originalDoc?.paperColor ?? undefined;

      setDocumentTypeChanges((prev) => {
        const next = new Map(prev);
        if (newType === originalType && normalizedPaperColor === originalPaperColor) {
          next.delete(id);
        } else {
          next.set(id, { type: newType, paperColor: normalizedPaperColor });
        }
        return next;
      });
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (!isPendingDocument(doc) && doc.id === id) {
            return { ...doc, type: newType, paperColor: normalizedPaperColor };
          }
          return doc;
        }),
      );
    } else {
      setLocalDocuments((prev) =>
        prev.map((doc) => {
          if (isPendingDocument(doc) && doc.tempId === id) {
            return { ...doc, documentType: newType, paperColor: normalizedPaperColor || null };
          }
          return doc;
        }),
      );
    }
  };

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'files':
        return renderFileName(row.files, row.id, row.createdAt);
      case 'description':
        return <span style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'block' }}>{row.description}</span>;
      case 'ref':
        return <span style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'block' }}>{row.ref}</span>;
      case 'documentType':
        return formatDocumentType(row.documentType);
      case 'paperColor':
        return formatPaperColor(row.paperColor, row.documentType);
      default:
        return row[column.field];
    }
  };

  const formatDocumentType = (type?: string) => {
    switch (type) {
      case 'ballot': return 'Ballot';
      case 'proof_of_claim': return 'Proof of Claim';
      case 'default': return 'Standard';
      default: return '';
    }
  };

  const formatPaperColor = (color?: string, docType?: string) => {
    if (docType === 'ballot') {
      return color === 'color' ? 'Colored' : 'Standard';
    }
    return 'Standard';
  };

  const renderFileName = (fileName: string, id: number, createdAt: string) => {
    return isLinkExpired(createdAt) ? (
      <a style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{fileName}</a>
    ) : (
      <a href="#" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }} onClick={() => onDownloadDocument(fileName, id)}>
        {fileName}
      </a>
    );
  };

  const showEditButton = isCosAdmin && isOnHoldStatus(jobStatus);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hasChanges = useMemo(() => {
    if (!isEditMode) return false;
    if (localDocuments.some(isPendingDocument)) return true;
    if (descriptionChanges.size > 0 || refNumberChanges.size > 0 || documentTypeChanges.size > 0) return true;
    const currentIds = localDocuments.map(d => isPendingDocument(d) ? d.tempId : d.id);
    if (currentIds.length !== originalDocumentIds.length) return true;
    return currentIds.some((id, index) => id !== originalDocumentIds[index]);
  }, [isEditMode, localDocuments, descriptionChanges, refNumberChanges, documentTypeChanges, originalDocumentIds]);
  const canSave = hasChanges && localDocuments.length > 0;

  // Report unsaved-changes state to the parent so the status dropdown can gate
  // the "Move to In Queue" transition (admins may only move a job to In Queue
  // once all document changes are saved).
  useEffect(() => {
    onUnsavedChangesChange?.(hasChanges);
  }, [hasChanges, onUnsavedChangesChange]);

  // Ensure the parent gate is reset when this component unmounts.
  useEffect(() => {
    return () => {
      onUnsavedChangesChange?.(false);
    };
  }, [onUnsavedChangesChange]);

  const handleDiscardChanges = () => {
    const docs = savedDocumentsRef.current ?? documents;
    setLocalDocuments([...docs]);
    setDescriptionChanges(new Map());
    setRefNumberChanges(new Map());
    setDocumentTypeChanges(new Map());
    setEditingField(null);
    setIsEditMode(false);
    setError(null);
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    if (!canSave) return false;

    const preEditDocuments = savedDocumentsRef.current ?? documents;

    // Validate: no empty descriptions on any document
    for (const doc of localDocuments) {
      if (isPendingDocument(doc)) {
        if (!doc.description || doc.description.trim().length === 0) {
          setError('All documents must have a description');
          return false;
        }
      } else {
        const desc = descriptionChanges.has(doc.id)
          ? descriptionChanges.get(doc.id)!
          : doc.docketDescription;
        if (!desc || desc.trim().length === 0) {
          setError('All documents must have a description');
          return false;
        }
      }
    }

    setIsSaving(true);
    setError(null);
    try {
      // Upload pending documents in parallel (max 3 concurrent)
      const pendingDocs = localDocuments.filter(isPendingDocument);
      const uploadedMap = new Map<string, JobDocument>();

      const uploadBatchSize = 3;
      for (let i = 0; i < pendingDocs.length; i += uploadBatchSize) {
        const batch = pendingDocs.slice(i, i + uploadBatchSize);
        const results = await Promise.all(
          batch.map(async (doc) => {
            const formData = new FormData();
            formData.append('file', doc.file);
            const response = await apiInstance.post(`/job/${jobId}/documents`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { tempId: doc.tempId, data: response.data as JobDocument };
          })
        );
        for (const result of results) {
          uploadedMap.set(result.tempId, result.data);
        }
      }

      // Build final document list replacing pending with uploaded
      const finalDocuments: JobDocument[] = localDocuments.map((doc) => {
        if (isPendingDocument(doc)) return uploadedMap.get(doc.tempId)!;
        return doc;
      });

      // Delete documents that were removed
      const finalIds = new Set(finalDocuments.map((d) => d.id));
      const deletedDocIds = originalDocumentIds.filter((id) => !finalIds.has(id));
      await Promise.all(deletedDocIds.map((docId) => apiInstance.delete(`/job/documents/${docId}`)));

      // Build all metadata updates
      const documentIds = finalDocuments.map((doc) => doc.id);

      const descriptionUpdates: { id: number; description: string }[] = [];
      for (const [id, description] of Array.from(descriptionChanges.entries())) {
        if (finalIds.has(id)) {
          descriptionUpdates.push({ id, description });
        }
      }
      for (const doc of localDocuments) {
        if (isPendingDocument(doc) && doc.description.trim()) {
          const uploaded = uploadedMap.get(doc.tempId);
          if (uploaded) {
            descriptionUpdates.push({ id: uploaded.id, description: doc.description.trim() });
          }
        }
      }

      const refNumberUpdates: { id: number; referenceNumber: string }[] = [];
      for (const [id, referenceNumber] of Array.from(refNumberChanges.entries())) {
        if (finalIds.has(id)) {
          refNumberUpdates.push({ id, referenceNumber });
        }
      }
      for (const doc of localDocuments) {
        if (isPendingDocument(doc) && doc.refNumber.trim()) {
          const uploaded = uploadedMap.get(doc.tempId);
          if (uploaded) {
            refNumberUpdates.push({ id: uploaded.id, referenceNumber: doc.refNumber.trim() });
          }
        }
      }

      const typeUpdates: { id: number; type: string; paperColor?: string }[] = [];
      for (const [id, change] of Array.from(documentTypeChanges.entries())) {
        if (finalIds.has(id) && change.type !== undefined) {
          typeUpdates.push({ id, type: change.type, paperColor: change.paperColor });
        }
      }
      for (const doc of localDocuments) {
        if (isPendingDocument(doc) && doc.documentType) {
          const uploaded = uploadedMap.get(doc.tempId);
          if (uploaded) {
            typeUpdates.push({ id: uploaded.id, type: doc.documentType, paperColor: doc.paperColor || undefined });
          }
        }
      }

      // Run reorder + descriptions + ref numbers + types in parallel
      await Promise.all([
        apiInstance.put(`/job/${jobId}/documents/reorder`, { documentIds }),
        descriptionUpdates.length > 0
          ? apiInstance.put(`/job/${jobId}/documents/descriptions`, { documents: descriptionUpdates })
          : Promise.resolve(null),
        refNumberUpdates.length > 0
          ? apiInstance.put(`/job/${jobId}/documents/reference-numbers`, { documents: refNumberUpdates })
          : Promise.resolve(null),
        typeUpdates.length > 0
          ? apiInstance.put(`/job/${jobId}/documents/types`, { documents: typeUpdates })
          : Promise.resolve(null),
      ]);

      // Update local state with the changes
      for (const update of descriptionUpdates) {
        const doc = finalDocuments.find(d => d.id === update.id);
        if (doc) doc.docketDescription = update.description;
      }
      for (const update of refNumberUpdates) {
        const doc = finalDocuments.find(d => d.id === update.id);
        if (doc) doc.docketReferenceNumber = update.referenceNumber;
      }
      for (const update of typeUpdates) {
        const doc = finalDocuments.find(d => d.id === update.id);
        if (doc) {
          doc.type = update.type;
          doc.paperColor = update.paperColor;
        }
      }

      // Update read-mode table data
      const transformedData = finalDocuments.map((document) => ({
        files: document.storedFile.filename,
        description: document.docketDescription,
        ref: document.docketReferenceNumber,
        documentType: document.type,
        paperColor: document.paperColor,
        pages: document.pageCount,
        id: document.id,
        createdAt: document.createdAt,
      }));
      setData(transformedData);
      savedDocumentsRef.current = finalDocuments;
      setDescriptionChanges(new Map());
      setRefNumberChanges(new Map());
      setDocumentTypeChanges(new Map());
      onDocumentsChange?.();
      hasSavedRef.current = true;

      // Only recalculate invoice if something price-affecting changed:
      // added/removed documents, type, paperColor. Description and ref number
      // changes are metadata-only and must not trigger the recalculation 
      const hasPriceAffectingChange =
        deletedDocIds.length > 0 ||
        pendingDocs.length > 0 ||
        typeUpdates.length > 0 ||
        localDocuments.length !== preEditDocuments.length;

      // Recalculate invoice after successful save
      try {
        if (!hasPriceAffectingChange) {
          // Metadata-only edit: create audit log with unchanged invoice total
          // by calling recalculate but signalling no price change
          await apiInstance.post(`/job/${jobId}/recalculate-invoice`, {
            previousState: {
              documents: preEditDocuments.map((doc, index) => ({
                filename: doc.storedFile.filename,
                type: doc.type || '',
                description: doc.docketDescription || '',
                ecfReference: doc.docketReferenceNumber || 'blank',
                pageCount: doc.pageCount || 0,
                position: index,
                ...(doc.paperColor ? { paperColor: doc.paperColor } : {}),
              })),
              invoiceTotal: currentInvoiceTotal ?? 0,
            },
            skipRecalculation: true,
          });
          onSaveComplete?.();
        } else {
          // Use the snapshot captured at the start of this save, before savedDocumentsRef was updated.
          const previousDocuments = preEditDocuments.map((doc, index) => ({
            filename: doc.storedFile.filename,
            type: doc.type || '',
            description: doc.docketDescription || '',
            ecfReference: doc.docketReferenceNumber || 'blank',
            pageCount: doc.pageCount || 0,
            position: index,
            ...(doc.paperColor ? { paperColor: doc.paperColor } : {}),
          }));

          const invoiceResponse = await apiInstance.post(`/job/${jobId}/recalculate-invoice`, {
            previousState: {
              documents: previousDocuments,
              invoiceTotal: currentInvoiceTotal ?? 0,
            },
          });
          onInvoiceRecalculated?.(invoiceResponse.data.data);
          onSaveComplete?.();
        }
      } catch (invoiceErr: any) {
        console.error('Invoice recalculation failed:', invoiceErr?.response?.data || invoiceErr?.message);
      }

      setIsEditMode(false);
      setSuccessMessage('Changes were saved successfully');

      return true;
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Error occurred when saving changes. Check your Internet connection and try again.';
      setError(message);
      // Refresh data from server to avoid stale/duplicate state after partial save
      onDocumentsChange?.();
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => !!hasChanges,
    hasActiveInput: () => editingField !== null,
    saveChanges: handleSaveChanges,
    discardChanges: handleDiscardChanges,
  }), [hasChanges, editingField, handleSaveChanges, handleDiscardChanges]);

  const handleToggleEditMode = () => {
    if (isEditMode) {
      setIsEditMode(false);
      setDescriptionChanges(new Map());
      setRefNumberChanges(new Map());
      setDocumentTypeChanges(new Map());
      setEditingField(null);
      setError(null);
    } else {
      handleEnterEditMode();
    }
  };

  // Calculate total size for 100MB validation
  const currentTotalSizeBytes = localDocuments.reduce((sum, doc) => {
    if (isPendingDocument(doc)) return sum + doc.file.size;
    return sum;
  }, 0);

  return (
    data && (
      <div style={{ backgroundColor: isEditMode ? 'rgba(67, 110, 178, 0.10)' : 'transparent', padding: isEditMode ? '1rem 1.25rem' : '0', margin: isEditMode ? '20px -1.25rem' : '0', marginTop: isEditMode ? '20px' : '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9375rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Typography
              sx={{
                fontWeight: '600',
              }}
            >
              Documents
            </Typography>
            {isCosAdmin && (
              <Tooltip
                title={
                  <span>
                    Edits can only be made to your job while it is In Queue:
                    <ul style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}>
                      <li>Same-day: Up to 15 minutes post-submission</li>
                      <li>Next business day: 12:10am PT</li>
                    </ul>
                    Please call or chat our team immediately if changes need to be made.
                  </span>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: { fontSize: '0.875rem', padding: '0.5rem 0.75rem', maxWidth: '20rem' },
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: '1.125rem', color: '#666', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </div>
          {showEditButton && <EditDocumentsButton onClick={handleToggleEditMode} label={isEditMode ? 'Close Edit Mode' : 'Edit Documents'} />}
        </div>

        {isEditMode ? (
          <>
            <EditableDocumentList
              documents={localDocuments}
              onAdd={handleAddDocument}
              onDelete={handleDeleteDocument}
              onLocalReorder={handleLocalReorder}
              onDescriptionChange={handleDescriptionChange}
              onRefNumberChange={handleRefNumberChange}
              onDocumentTypeChange={handleDocumentTypeChange}
              isUploading={false}
              currentTotalSizeBytes={currentTotalSizeBytes}
              editingField={editingField}
              onEditingFieldChange={setEditingField}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
              <button
                onClick={hasChanges ? handleDiscardChanges : undefined}
                disabled={!hasChanges}
                style={{
                  background: 'none',
                  border: 'none',
                  color: hasChanges ? '#d32f2f' : '#ACACAC',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: hasChanges ? 'pointer' : 'not-allowed',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Discard Changes
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={!canSave || isSaving}
                style={{
                  backgroundColor: canSave ? '#436EB2' : '#9e9e9e',
                  color: '#fff',
                  border: 'none',
                  padding: '0.625rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: canSave && !isSaving ? 'pointer' : 'not-allowed',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : (
          <BkTable
            renderCell={renderCell}
            renderPagination={false}
            initialRowsPerPage={20}
            data={data}
            columns={columns}
          />
        )}

        <BkSnackBar
          open={!!error}
          snackMessage={error || ''}
          handleCloseSnackbar={() => setError(null)}
          severity={error === 'Job is no longer On Hold' ? 'info' : 'error'}
        />
        <BkSnackBar
          open={!!successMessage}
          snackMessage={successMessage || ''}
          handleCloseSnackbar={() => setSuccessMessage(null)}
          severity="success"
        />
      </div>
    )
  );
});

export default JobDocuments;
